import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

export default function StoichiometryCalculator() {
  const [reactantMoles, setReactantMoles] = useState("");
  const [reactantRatio, setReactantRatio] = useState("");
  const [productRatio, setProductRatio] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const moles = parseFloat(reactantMoles);
    const ratioA = parseFloat(reactantRatio);
    const ratioB = parseFloat(productRatio);

    if (isNaN(moles) || isNaN(ratioA) || isNaN(ratioB) || ratioA === 0) {
      toast.error("Please enter valid values");
      return;
    }

    const productMoles = (moles * ratioB) / ratioA;
    setResult(productMoles);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setReactantMoles("");
    setReactantRatio("");
    setProductRatio("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Stoichiometry Calculator" 
      calculatorPath="/calculator/stoichiometry"
      category="chemistry"
      subcategory="Stoichiometry"
    >
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Mole Ratio Calculation</CardTitle>
              <CardDescription>
                Convert between reactants and products using balanced equation ratios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reactantMoles">Reactant Moles</Label>
                  <Input
                    id="reactantMoles"
                    type="number"
                    step="0.001"
                    placeholder="Enter moles of reactant"
                    value={reactantMoles}
                    onChange={(e) => setReactantMoles(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reactantRatio">Reactant Coefficient</Label>
                  <Input
                    id="reactantRatio"
                    type="number"
                    step="1"
                    placeholder="From balanced equation"
                    value={reactantRatio}
                    onChange={(e) => setReactantRatio(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="productRatio">Product Coefficient</Label>
                  <Input
                    id="productRatio"
                    type="number"
                    step="1"
                    placeholder="From balanced equation"
                    value={productRatio}
                    onChange={(e) => setProductRatio(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculate} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {result !== null && (
                <>
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Product Moles</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {result.toFixed(4)} mol
                      </p>
                    </CardContent>
                  </Card>

                  <CalculatorSuperCalcCTA config={calculatorCTAs["stoichiometry"]} />
                </>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter the moles of your starting reactant</p>
                  <p>2. Enter the coefficient of the reactant from the balanced equation</p>
                  <p>3. Enter the coefficient of the product from the balanced equation</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 2H₂ + O₂ → 2H₂O | 4 mol H₂ (coeff 2) → 2 mol O₂ (coeff 1)
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
